import { useEffect, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import type { PagaebleResult, ReportRPALog, ReportRPALogDetalle, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import { AUTOMATIONS_PATH, errorMessage, formatSlashDateTime, pageWindow, personName } from '../common/automation-common'

const PAGE_SIZE = 20
const LOAD_ERROR = 'No se pudo cargar el resultado del proceso.'

export type DetailKind = 'ok' | 'review' | 'error' | 'pending'

export interface AutomationDetailRow {
    id: number
    kind: DetailKind
    noPoliza: string
    polizaId: number | null
    ramo: string
    clienteId: number | null
    cliente: string
    resultado: string
    warnings: string[]
}

export interface AutomationSummary {
    aseguradora: string
    fecha: string
    total: number
}

const toRow = (item: ReportRPALogDetalle): AutomationDetailRow => {
    const status = String(item.status || '').toLowerCase()
    const cliente = item.poliza?.cliente || null
    const warnings = [
        ...(item.polizaEsRobot ? ['Es necesario validar la póliza'] : []),
        ...(item.clienteEsRobot ? ['El cliente es nuevo y es necesario validarlo'] : []),
    ]
    const kind: DetailKind = status === 'error' ? 'error' : status === 'pendiente' ? 'pending' : warnings.length > 0 ? 'review' : 'ok'
    return {
        id: item.id,
        kind,
        noPoliza: item.poliza?.noPoliza || item.noPoliza || '—',
        polizaId: item.poliza?.id ?? null,
        ramo: item.poliza?.ramo?.ramo || '',
        clienteId: cliente?.id ?? null,
        cliente: cliente ? personName(cliente) : '',
        resultado: item.resultado || '',
        warnings,
    }
}

export const useAutomationViewModel = () => {
    const location = useLocation()
    const { logId } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const backUrl: string = (location.state as { backUrl?: string } | null)?.backUrl || AUTOMATIONS_PATH
    const page = Math.max(1, Number(searchParams.get('page')) || 1)

    const [summary, setSummary] = useState<AutomationSummary | null>(null)
    const [loadingLog, setLoadingLog] = useState(true)
    const [loadingDetail, setLoadingDetail] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [detail, setDetail] = useState<ReportRPALogDetalle[]>([])
    const [totalItems, setTotalItems] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [detailId, setDetailId] = useState<number | null>(null)

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoadingLog(true)
                const payload: WrapperResponse<ReportRPALog | null> | null = await (await APIInvoke.invokeGET(`/rpa/logs/${logId}`)).json()
                if (!active) return
                if (!payload?.ok) throw new Error(payload?.message || LOAD_ERROR)
                const log = payload.body
                setSummary(log ? {
                    aseguradora: log.aseguradora?.aseguradora || log.aseguradora?.abreviacion || '—',
                    fecha: formatSlashDateTime(log.fechaTermino) || formatSlashDateTime(log.fechaPrograma) || '—',
                    total: Number(log.totalRegistros) || 0,
                } : null)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoadingLog(false)
            }
        }
        load()
        return () => { active = false }
    }, [logId])

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoadingDetail(true)
                const payload: WrapperResponse<PagaebleResult<ReportRPALogDetalle> | null> | null = await (await APIInvoke.invokeGET(`/rpa/logs/${logId}/detalle?page=${page}&size=${PAGE_SIZE}`)).json()
                if (!active) return
                if (!payload?.ok) throw new Error(payload?.message || LOAD_ERROR)
                setDetail(payload.body?.items || [])
                setTotalItems(payload.body?.totalItems || 0)
                setTotalPages(payload.body?.totalPages || 0)
            } catch (loadError) {
                if (active) {
                    setDetail([])
                    setError(errorMessage(loadError, LOAD_ERROR))
                }
            } finally {
                if (active) setLoadingDetail(false)
            }
        }
        load()
        return () => { active = false }
    }, [logId, page])

    const rows = detail.map(toRow)

    return {
        summary,
        loading: loadingLog || loadingDetail,
        error,
        rows,
        counts: {
            ok: rows.filter((row) => row.kind === 'ok').length,
            review: rows.filter((row) => row.kind === 'review').length,
            error: rows.filter((row) => row.kind === 'error').length,
            pending: rows.filter((row) => row.kind === 'pending').length,
        },
        page,
        totalItems,
        totalPages,
        pages: totalPages > 0 ? pageWindow(page, totalPages) : [],
        backTo: backUrl,
        detailId,
        clientePath: (id: number) => `/clientes/${id}`,
        setPage: (next: number) => setSearchParams({ page: String(next) }),
        openPoliza: (id: number) => setDetailId(id),
        closePoliza: () => setDetailId(null),
    }
}
